// ─────────────────────────────────────────────────────────────────────────────
// Property Type Display
// ─────────────────────────────────────────────────────────────────────────────

const PROPERTY_TYPE_LABELS: Record<string, string> = {
  'single-family': 'Single Family Residential',
  'multi-family': 'Multifamily Residential',
  'vacation-rental': 'Vacation Rental',
  'luxury': 'Luxury Residential',
  'beachfront': 'Beachfront Property',
  'commercial': 'Commercial',
};

export const getPropertyTypeDisplay = (type: string): string => {
  return PROPERTY_TYPE_LABELS[type] || type;
};

// ─────────────────────────────────────────────────────────────────────────────
// Asset Type Colors — global color mapping for charts, legends, and badges
// ─────────────────────────────────────────────────────────────────────────────

export const ASSET_TYPE_COLORS: Record<string, string> = {
  'Multifamily':       '#2D5BFF', // blue
  'Commercial Office': '#14B8A6', // teal
  'Retail':            '#F59E0B', // amber
  'Industrial':        '#8B5CF6', // violet
  'Hospitality':       '#EC4899', // pink
  'Mixed Use':         '#10B981', // emerald
  'Land':              '#78716C', // stone
  'Single Family':     '#0EA5E9', // sky
  'Self Storage':      '#D946EF', // fuchsia
};

export const getAssetTypeColor = (type: string): string => {
  return ASSET_TYPE_COLORS[type] || '#6B6B6B';
};

// ─────────────────────────────────────────────────────────────────────────────
// Currency Formatting
// ─────────────────────────────────────────────────────────────────────────────

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const formatCurrency = (amount: number): string => {
  return currencyFormatter.format(amount);
};

/**
 * Format currency in compact form (e.g., $2.5M, $25K)
 */
export const formatCurrencyCompact = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return currencyFormatter.format(amount);
};

// ─────────────────────────────────────────────────────────────────────────────
// Share Calculation Helpers
// ─────────────────────────────────────────────────────────────────────────────

export const calculateValidShares = (
  shares: number,
  availableShares: number,
  minShares: number = 1
): number => {
  return Math.max(minShares, Math.min(availableShares, shares));
};

export const calculateSharesFromAmount = (
  amount: number,
  sharePrice: number,
  availableShares: number,
  minShares: number = 1
): number => {
  if (!Number.isFinite(amount) || amount < 0) {
    return minShares;
  }
  const shares = Math.floor(amount / sharePrice);
  return calculateValidShares(shares, availableShares, minShares);
};
